import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { assetsApi } from "@/api/assets";

/**
 * Imagens de referência de UMA tarefa de arte (one-off), escolhidas na criação.
 *
 * Por que existe: a biblioteca de marca (KB) é o acervo PERSISTENTE; aqui o
 * usuário sobe insumos específicos DESTA tarefa — a foto exata do sujeito que
 * deve aparecer e/ou exemplos de estilo a seguir. Não polui a biblioteca e não
 * gasta token extra: viram `executionState.selectedReferenceAssetIds` e entram
 * como reference na geração (o gerador preserva o sujeito e imita o estilo).
 *
 * Valor = array de asset.id. Upload direto do computador (vira asset da company).
 */
const MAX_REFS = 6;

export function TaskArtImageInputs({
  companyId,
  value,
  onChange,
}: {
  companyId: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    const next = [...value];
    for (const file of Array.from(files)) {
      if (next.length >= MAX_REFS) break;
      try {
        const res = await assetsApi.uploadImage(companyId, file);
        next.push(res.assetId);
      } catch (err) {
        setError(`Não consegui subir "${file.name}": ${(err as Error).message}`);
      }
    }
    onChange(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
        Imagens de referência (opcional)
      </span>
      <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -4, lineHeight: 1.5 }}>
        Suba a foto do sujeito que deve aparecer (pessoa, produto) e/ou exemplos
        de estilo que a IA deve seguir. Valem só pra esta tarefa.
      </span>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUpload(e.target.files)}
      />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        {value.map((id) => (
          <div key={id} style={{ position: "relative" }}>
            <img
              src={`/api/assets/${id}/content`}
              alt="referência"
              style={{
                width: 96,
                height: 96,
                objectFit: "cover",
                borderRadius: 10,
                border: "1px solid var(--wf-ink-5)",
              }}
            />
            <button
              type="button"
              onClick={() => remove(id)}
              title="Remover"
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 22,
                height: 22,
                borderRadius: "50%",
                border: "none",
                background: "var(--nd-orange)",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {value.length < MAX_REFS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{
              width: 96,
              height: 96,
              borderRadius: 10,
              border: "1.5px dashed var(--wf-ink-5)",
              background: "transparent",
              color: "var(--wf-ink-3)",
              cursor: uploading ? "default" : "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontSize: 11,
              textAlign: "center",
              padding: 6,
            }}
          >
            <ImagePlus size={20} />
            {uploading ? "enviando…" : "adicionar"}
          </button>
        )}
      </div>

      {error && (
        <p style={{ fontSize: 11.5, color: "#e5484d", margin: 0, fontWeight: 500 }}>{error}</p>
      )}
    </div>
  );
}
